import { format } from 'date-fns'

import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { InputFormValuesEdit } from '../types'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useNotification } from '@/ui/Notification'
import { useCombinedLogsTableFormStateStore } from '../store'
import { ICreateControllerLogRequest, RouteSelectionMode, Log, IRtnTripAsString } from '@/types/logs'
import { useAuthStore } from '@/store/auth-store'
import { useGarageStore } from '@/modules/FindGarage'
import { Role } from '@/types/auth'
import { formatCreateLogDate } from '../helpers/formatCreateLogDate'
import { useDateStore } from '@/components/DateInput'
import { isValidTimeFormat } from '@/helpers/isValidTimeFormat'
import { useGetCodes } from './useGetCodes'
import { formatLogRequestBody } from '../helpers/formatLogRequestBody'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'
import { getCodeTypeId } from '../helpers/getCodeTypeId'
import { useGetUserInfo } from '@/modules/Auth'

interface ICreateControllerLogResponse {
    id: string
}

export const useUpdateControllerLog = () => {
    const queryClient = useQueryClient()
    const { routes, workstation } = useWorkstationStore()
    const { garage } = useGarageStore()
    const { showNotification } = useNotification()
    const { selectedTrips, selectedOriginalTrips, report, mechanicalInfo, locationType, isBulkCurtailment } =
        useCombinedLogsTableFormStateStore()
    const { role } = useAuthStore()
    const { date } = useDateStore()
    const logParams = useGetControllerLogParams()
    const { data: codes = [] } = useGetCodes()
    const { data: userInfo } = useGetUserInfo()

    const updateControllerLogMutation = useAppMutation<
        ICreateControllerLogResponse,
        Error,
        ICreateControllerLogRequest & { selectedRowId: string },
        Log[]
    >({
        mutationFn: ({ selectedRowId, ...data }) =>
            createInstance({
                url: `/controller-logs/${selectedRowId}`,
                method: 'PUT',
                data,
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: ICreateControllerLogRequest, previousLogs?: Log[]) => {
            const updatedLog = formatLogRequestBody({ requestBody, codes })
            return previousLogs?.map((item) =>
                item.id === updatedLog.id ? { ...updatedLog, originalAuthor: item.originalAuthor } : item
            )
        },
        onSuccess: async (data) => {
            queryClient.setQueriesData<Log[]>({ queryKey: ['controllerLogs'] }, (previousLogs) => {
                if (!previousLogs) return previousLogs

                return previousLogs.map((log) => (data.id === log.id ? { ...log, ...data } : log))
            })

            await queryClient.invalidateQueries({ queryKey: ['unfinishedLogs'] })
            showNotification({ message: 'Record updated' })
        },
    })

    const handleUpdateControllerLog = (
        data: InputFormValuesEdit,
        selectedRowId: string,
        previousTimeStamp?: string
    ) => {
        if (!selectedRowId) throw new Error('No selected row id')

        const isWorkstationSelected =
            role === Role.MileageClerk ? !!garage?.routes.length : !!workstation?.workstationNumber

        const selectedRoutes = role === Role.MileageClerk ? garage?.routes : routes.map((route) => route.route)

        const selectedTimeStamp = isValidTimeFormat(data.timeStamp)
            ? data.timeStamp
            : previousTimeStamp && isValidTimeFormat(previousTimeStamp)
              ? previousTimeStamp
              : format(new Date(), 'HH:mm')

        const timeStamp = formatCreateLogDate(date, selectedTimeStamp)

        const performUpdate = () => {
            updateControllerLogMutation.mutate({
                selectedRowId,
                id: selectedRowId,
                code: data.code ? data.code : undefined,
                codeTypeId: getCodeTypeId(data.code),
                reference: data.reference,
                routes: !isWorkstationSelected
                    ? undefined
                    : data.routes && data.routes !== 'N/A'
                      ? data.routes.split(', ')
                      : selectedRoutes,
                routeSelectionMode: data.routes
                    ? data.routes === 'N/A'
                        ? RouteSelectionMode.All
                        : RouteSelectionMode.Default
                    : RouteSelectionMode.None,
                trip: data.trip,
                rtnTrips:
                    !!selectedTrips.length && !isBulkCurtailment
                        ? selectedTrips.map(
                              (trip) => ({ trip: String(trip.trip), order: trip.order }) as IRtnTripAsString
                          )
                        : data.rtnTrips
                          ? (String(data.rtnTrips)
                                .split(',')
                                .map((trip) => ({ trip: trip, order: 0 })) as IRtnTripAsString[])
                          : undefined,
                run: data.run ? data.run : undefined,
                garageCode: data.depotCode ? data.depotCode : undefined,
                duty: data.duty ? data.duty.split('/') : undefined,
                fleet: data.fleet ? data.fleet.split('/') : undefined,
                locationFrom: data.locationFrom,
                locationTo: data.locationTo,
                locationType: locationType ?? undefined,
                description: data.description ? data.description : undefined,
                extraInfo: data.extraInfo ? data.extraInfo : undefined,
                strategy: data.strategy ? data.strategy : undefined,
                delay: data.delay ? data.delay : undefined,
                mileage: data.mileage ? +data.mileage : undefined,
                isZeroMileage: data.isZeroMileage === 'true',
                author: userInfo?.lastSessionDetails.authorName ?? undefined,
                timeStamp,
                workstation: workstation?.workstationNumber,
                report,
                mechanicalInfo,
                originalTrip:
                    data.code === 'E'
                        ? {
                              routes: data.originalRoutes
                                  ? data.originalRoutes === 'N/A'
                                      ? selectedRoutes
                                      : [data.originalRoutes]
                                  : undefined,
                              trip: data.originalTrip,
                              rtnTrips:
                                  selectedOriginalTrips.length > 0
                                      ? selectedOriginalTrips.map(
                                            (trip) =>
                                                ({
                                                    trip: String(trip.trip),
                                                    order: trip.order,
                                                }) as IRtnTripAsString
                                        )
                                      : data.originalRtnTrips
                                        ? data.originalRtnTrips
                                              .split(',')
                                              .map((trip) => ({ trip: trip, order: 0 }) as IRtnTripAsString)
                                        : undefined,
                              run: data.originalRun ? data.originalRun : undefined,
                              duty: data.originalDuty ? data.originalDuty.split('/') : undefined,
                              fleet: data.originalFleet ? data.originalFleet.split('/') : undefined,
                          }
                        : undefined,
                eventLogId: data.eventLogId,
                selectedCodeTypeReasonId: data.selectedCodeTypeReasonId || '',
                direction: data.direction,
                bulkCurtailment:
                    data.bulkCurtailmentTripTo || isBulkCurtailment
                        ? {
                              tripTo: data.bulkCurtailmentTripTo?.toString() || '',
                              runTo: data.bulkCurtailmentRunTo?.toString() || '',
                              dutyTo: data.bulkCurtailmentDutyTo?.toString() || '',
                              fleetTo: data.bulkCurtailmentFleetTo?.toString() || '',
                          }
                        : undefined,
            })
        }

        if (!isValidTimeFormat(data.timeStamp)) {
            showNotification({
                type: 'error',
                title: 'Time cannot be blank',
                message: `Your updates to ${data.reference} will be saved with the time ${selectedTimeStamp} on ${format(timeStamp, 'dd MMM yyyy')} because the time field cannot be left blank.`,
                manualDismiss: true,
                errorDismissTitle: 'Close',
                onDismiss: performUpdate,
            })
        } else {
            performUpdate()
        }
    }

    return { handleUpdateControllerLog, isPending: updateControllerLogMutation.isPending }
}
